import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
  ResponsiveDialogBody,
  ResponsiveDialogFooter,
  useResponsiveDialog,
} from "@/components/ui/responsive-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { demoRequestSchema, DemoRequestFormData } from "@/lib/validations/demo-request";
import { useDemoRequest } from "@/hooks/use-demo-request";
import { useIsMobile } from "@/hooks/use-mobile";
import { FormStepIndicator } from "@/components/ui/form-step-indicator";
import { cn } from "@/lib/utils";
import { hasAnalyticsConsent } from "@/hooks/useCookieConsent";

interface DemoRequestDialogProps {
  trigger: React.ReactNode;
  selectedPlan?: "Standard" | "Founders";
  context?: "hero" | "pricing" | "cta" | "demo" | "header";
}

const STEP_TITLES = [
  "Vos coordonnées",
  "Votre établissement", 
  "Finalisation",
];

const DemoRequestDialog = ({ trigger, selectedPlan, context }: DemoRequestDialogProps) => {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const formRef = useRef<HTMLFormElement>(null);
  
  const { mutate: submitRequest, isPending } = useDemoRequest();

  const form = useForm<DemoRequestFormData>({
    resolver: zodResolver(demoRequestSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      establishmentName: "",
      establishmentType: "non_renseigne",
      seatingCapacity: undefined,
      city: "",
      interestedPlan: selectedPlan ? selectedPlan.toLowerCase() as "standard" | "founders" : "undecided",
      message: "",
      contactPreference: "any",
      honeypot: "",
    },
  });

  // Reset step when dialog closes
  useEffect(() => {
    if (!open) {
      setCurrentStep(1);
    }
  }, [open]);

  // Auto-scroll on input focus for mobile
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isMobile) {
      setTimeout(() => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const onSubmit = (data: DemoRequestFormData) => {
    // Track Meta Pixel Lead event (only if consent granted)
    if (typeof window.fbq === 'function' && hasAnalyticsConsent()) {
      window.fbq('track', 'Lead', {
        content_name: 'Demo Request Dialog',
        content_category: data.interestedPlan,
      });
    }

    submitRequest(
      { ...data, context },
      {
        onSuccess: () => {
          setOpen(false);
          form.reset();
          navigate("/merci");
        },
      }
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
      setCurrentStep(1);
    }
    setOpen(newOpen);
  };

  // Validate current step fields before advancing
  const validateStep = async (step: number): Promise<boolean> => {
    const fieldsToValidate: (keyof DemoRequestFormData)[] = [];
    
    if (step === 1) {
      fieldsToValidate.push('fullName', 'email');
    }
    // Step 2 has no required fields
    // Step 3 validates on submit
    
    if (fieldsToValidate.length === 0) return true;
    
    const result = await form.trigger(fieldsToValidate);
    return result;
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid && currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  // Mobile step content
  const renderMobileStepContent = () => {
    const inputClassName = "h-12 text-base";
    
    return (
      <div className="space-y-4">
        {currentStep === 1 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-4">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom complet *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Jean Dupont" 
                      className={inputClassName}
                      onFocus={handleInputFocus}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input 
                      type="email" 
                      placeholder="jean@exemple.ch" 
                      className={inputClassName}
                      onFocus={handleInputFocus}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Téléphone</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="+41 79 123 45 67" 
                      className={inputClassName}
                      onFocus={handleInputFocus}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {currentStep === 2 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-4">
            <FormField
              control={form.control}
              name="establishmentName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom de l'établissement</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Café du Centre" 
                      className={inputClassName}
                      onFocus={handleInputFocus}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="establishmentType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type d'établissement</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className={inputClassName}>
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="non_renseigne">Non renseigné</SelectItem>
                      <SelectItem value="cafe">Café</SelectItem>
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="brasserie">Brasserie</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="seatingCapacity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre de places</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder="50" 
                      className={inputClassName}
                      onFocus={handleInputFocus}
                      {...field}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ville</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Lausanne" 
                      className={inputClassName}
                      onFocus={handleInputFocus}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {currentStep === 3 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-4">
            <FormField
              control={form.control}
              name="interestedPlan"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plan d'intérêt</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className={inputClassName}>
                        <SelectValue placeholder="Sélectionnez un plan" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="standard">Offre Standard - 49 CHF/mois</SelectItem>
                      <SelectItem value="founders">Offre Printemps 2026 - 29 CHF à vie</SelectItem>
                      <SelectItem value="undecided">Je ne sais pas encore</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message (optionnel)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Parlez-nous de vos besoins..."
                      className="resize-none text-base min-h-[80px]"
                      rows={3}
                      onFocus={handleInputFocus as any}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactPreference"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Comment souhaitez-vous être contacté ?</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-wrap gap-4"
                    >
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="email" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Par email
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="phone" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Par téléphone
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="any" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Peu importe
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
    );
  };

  // Desktop form (unchanged)
  const renderDesktopForm = () => (
    <>
      {/* Honeypot field - hidden from users, catches bots */}
      <FormField
        control={form.control}
        name="honeypot"
        render={({ field }) => (
          <FormItem className="hidden">
            <FormLabel>Leave this field empty</FormLabel>
            <FormControl>
              <Input {...field} tabIndex={-1} autoComplete="off" />
            </FormControl>
          </FormItem>
        )}
      />
      
      <div className="grid md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nom complet *</FormLabel>
              <FormControl>
                <Input placeholder="Jean Dupont" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jean@exemple.ch" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Téléphone</FormLabel>
            <FormControl>
              <Input placeholder="+41 79 123 45 67" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="establishmentName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nom de l'établissement</FormLabel>
              <FormControl>
                <Input placeholder="Café du Centre" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="establishmentType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type d'établissement</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="non_renseigne">Non renseigné</SelectItem>
                  <SelectItem value="cafe">Café</SelectItem>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                  <SelectItem value="brasserie">Brasserie</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="seatingCapacity"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre de places</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  placeholder="50" 
                  {...field}
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Ville</FormLabel>
              <FormControl>
                <Input placeholder="Lausanne" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="interestedPlan"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Plan d'intérêt</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez un plan" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="standard">Offre Standard - 49 CHF/mois</SelectItem>
                <SelectItem value="founders">Offre Printemps 2026 - 29 CHF à vie</SelectItem>
                <SelectItem value="undecided">Je ne sais pas encore</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="message"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Message (optionnel)</FormLabel>
            <FormControl>
              <Textarea 
                placeholder="Parlez-nous de vos besoins..."
                className="resize-none"
                rows={3}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="contactPreference"
        render={({ field }) => (
          <FormItem className="space-y-3">
            <FormLabel>Comment souhaitez-vous être contacté ?</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex flex-col space-y-1"
              >
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="email" />
                  </FormControl>
                  <FormLabel className="font-normal">
                    Par email
                  </FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="phone" />
                  </FormControl>
                  <FormLabel className="font-normal">
                    Par téléphone
                  </FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="any" />
                  </FormControl>
                  <FormLabel className="font-normal">
                    Peu importe
                  </FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <ResponsiveDialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(false)}
          disabled={isPending}
          className="w-full sm:w-auto"
        >
          Annuler
        </Button>
        <Button type="submit" disabled={isPending} className="w-full sm:w-auto">
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Envoyer la demande
        </Button>
      </ResponsiveDialogFooter>
    </>
  );

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogTrigger asChild>
        {trigger}
      </ResponsiveDialogTrigger>
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>
            Demander votre premier mois gratuit sans engagement
          </ResponsiveDialogTitle>
          {!isMobile && (
            <ResponsiveDialogDescription>
              Remplissez ce formulaire et nous vous contacterons rapidement pour démarrer votre essai gratuit.
            </ResponsiveDialogDescription>
          )}
          {isMobile && (
            <div className="mt-3">
              <FormStepIndicator
                currentStep={currentStep}
                totalSteps={3}
                stepTitles={STEP_TITLES}
              />
            </div>
          )}
          {!isMobile && (
            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4 shrink-0" />
                Préférez-vous réserver directement un appel ?
              </span>
              <a 
                href="https://calendly.com/maxdmnt/piktable" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Planifier un rendez-vous →
              </a>
            </p>
          )}
        </ResponsiveDialogHeader>

        <Form {...form}>
          <form 
            ref={formRef}
            onSubmit={form.handleSubmit(onSubmit)} 
            className={cn(
              isMobile ? "flex flex-col flex-1 min-h-0" : "space-y-4"
            )}
          >
            {/* Honeypot field - hidden from users, catches bots */}
            <FormField
              control={form.control}
              name="honeypot"
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormLabel>Leave this field empty</FormLabel>
                  <FormControl>
                    <Input {...field} tabIndex={-1} autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />

            {isMobile ? (
              <>
                <ResponsiveDialogBody>
                  {renderMobileStepContent()}
                </ResponsiveDialogBody>
                
                <ResponsiveDialogFooter>
                  <div className="flex gap-3 w-full">
                    {currentStep === 1 ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        className="flex-1 h-12"
                      >
                        Annuler
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handlePrevious}
                        className="flex-1 h-12"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Retour
                      </Button>
                    )}
                    
                    {currentStep < 3 ? (
                      <Button
                        type="button"
                        onClick={handleNext}
                        className="flex-1 h-12"
                      >
                        Suivant
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    ) : (
                      <Button 
                        type="submit" 
                        disabled={isPending}
                        className="flex-1 h-12"
                      >
                        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Envoyer
                      </Button>
                    )}
                  </div>
                </ResponsiveDialogFooter>
              </>
            ) : (
              renderDesktopForm()
            )}
          </form>
        </Form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};

export default DemoRequestDialog;
